class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Bundelkorting toepassen bij "Afrekenen" vanuit de drawer.
    // Gedelegeerd: de checkoutknop wordt bij elke re-render vervangen, dit element blijft bestaan.
    this.addEventListener('click', (evt) => {
      const btn = evt.target.closest('#CartDrawer-Checkout');
      if (!btn || btn.disabled || btn.dataset.processing) return;
      evt.preventDefault();
      btn.dataset.processing = '1';
      btn.classList.add('loading');
      this.applyCartBundleDiscountAndCheckout(btn);
    });
  }

  // Hoeveelheid-gebaseerde bundelkorting over het hele mandje: 2 = 10%, 3 = 15%, 4+ = 20%.
  cartBundleTier(itemCount) {
    if (itemCount >= 4) return 20;
    if (itemCount >= 3) return 15;
    if (itemCount >= 2) return 10;
    return 0;
  }

  async applyCartBundleDiscountAndCheckout(btn) {
    try {
      const r = await fetch('/cart.js');
      if (!r.ok) { this.goToCheckout(); return; }
      const cart = await r.json();

      const tier = this.cartBundleTier(cart.item_count);
      if (!tier || !cart.items.length) { this.goToCheckout(); return; }

      const variantIds = cart.items.map((i) => i.variant_id);
      const resp = await fetch('https://web-dmb-rands.vercel.app/api/create-bundle-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variantIds, discountPercent: tier })
      });
      if (!resp.ok) { this.goToCheckout(); return; }

      const { code } = await resp.json();
      if (code) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discounts: [code] })
        }).catch(() => {});
        this.goToCheckout(code);
      } else {
        this.goToCheckout();
      }
    } catch (_) {
      this.goToCheckout();
    }
  }

  goToCheckout(discountCode) {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/cart';
    const checkoutInput = document.createElement('input');
    checkoutInput.type = 'hidden';
    checkoutInput.name = 'checkout';
    checkoutInput.value = '';
    form.appendChild(checkoutInput);
    if (discountCode) {
      const discountInput = document.createElement('input');
      discountInput.type = 'hidden';
      discountInput.name = 'discount';
      discountInput.value = discountCode;
      form.appendChild(discountInput);
    }
    document.body.appendChild(form);
    form.submit();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Compacte bundel-upsell in de drawer: voegt een add-on toe met dezelfde
// _bundle_* line-item-properties als hd-bundle-builder, zodat dezelfde
// server-side BUNDLE-*-korting geldt. Ververst daarna de drawer.
class DsBundleUpsell extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.errEl = this.querySelector('.ds-bundle-upsell__err');
    this.modal = this.querySelector('.ds-bu-modal');

    this.addEventListener('click', (event) => {
      // Snel toevoegen via de +-pill of via de modal-knop.
      const addBtn = event.target.closest('.ds-bundle-upsell__btn, .ds-bu-modal__add');
      if (addBtn) {
        event.preventDefault();
        this.addItem(addBtn);
        return;
      }
      // Sluit de detail-popup (overlay of kruisje).
      if (event.target.closest('[data-close]')) {
        event.preventDefault();
        this.closeModal();
        return;
      }
      // Open de detail-popup vanuit een productkaart.
      const openBtn = event.target.closest('.ds-bundle-upsell__open');
      if (openBtn) {
        event.preventDefault();
        this.openModal(openBtn);
      }
    });

    // Escape sluit de popup.
    this.addEventListener('keyup', (event) => {
      if (event.key === 'Escape' && this.modal && !this.modal.hidden) this.closeModal();
    });

    // Desktop: verticaal muiswiel → horizontaal scrollen door de kaarten.
    this.scroller = this.querySelector('.ds-bundle-upsell__scroller');
    if (this.scroller) {
      this.scroller.addEventListener('wheel', (event) => {
        const canScroll = this.scroller.scrollWidth > this.scroller.clientWidth;
        // Alleen ingrijpen bij overzichtelijk verticale scroll (trackpad-horizontaal werkt al native).
        if (!canScroll || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
        event.preventDefault();
        this.scroller.scrollLeft += event.deltaY;
      }, { passive: false });
    }

    // Fase 1: relevante aanbevelingen o.b.v. de mand-inhoud (Shopify Recommendations API,
    // intent=related). Vervangt de server-side collectie-kaarten alleen als het lukt.
    this.loadRecommendations();
  }

  async loadRecommendations() {
    const base = this.dataset.recommendUrl;
    const pids = (this.dataset.cartProductIds || '').split(',').filter(Boolean);
    if (!base || !pids.length) return; // val terug op de server-side collectie-kaarten

    const limit = parseInt(this.dataset.limit, 10) || 6;
    const inCart = new Set((this.dataset.cartHandles || '').split(',').filter(Boolean));
    const anchors = [...new Set(pids)].slice(0, 3); // max 3 API-calls
    const seen = new Set();
    const picked = [];

    for (const id of anchors) {
      if (picked.length >= limit) break;
      try {
        const resp = await fetch(`${base}.json?product_id=${id}&intent=related&limit=${limit}`);
        if (!resp.ok) continue;
        const data = await resp.json();
        for (const p of data.products || []) {
          if (picked.length >= limit) break;
          if (!p || seen.has(p.id) || inCart.has(p.handle) || !p.featured_image) continue;
          const variant = (p.variants || []).find((v) => v.available) || (p.variants || [])[0];
          if (!variant) continue;
          seen.add(p.id);
          picked.push({ product: p, variant });
        }
      } catch (_) { /* stil: fallback blijft staan */ }
    }

    if (picked.length >= 2) this.renderCards(picked);
  }

  renderCards(list) {
    const scroller = this.querySelector('.ds-bundle-upsell__scroller');
    if (!scroller) return;
    scroller.innerHTML = list.map(({ product, variant }) => {
      const title = this.esc(product.title);
      const price = this.money(variant.price);
      const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
      const compareAttr = hasCompare ? `data-compare="${this.esc(this.money(variant.compare_at_price))}"` : '';
      const img = this.esc(this.sizedImage(product.featured_image, 200));
      const bigImg = this.esc(this.sizedImage(product.featured_image, 600));
      const url = this.esc(product.url || '#');
      return `
        <div class="ds-bundle-upsell__card" role="listitem">
          <button type="button" class="ds-bundle-upsell__open"
            data-variant-id="${variant.id}"
            data-title="${title}"
            data-price="${this.esc(price)}"
            ${compareAttr}
            data-img="${bigImg}"
            data-url="${url}"
            data-desc=""
            aria-label="Bekijk details van ${title}">
            <span class="ds-bundle-upsell__media">
              <img class="ds-bundle-upsell__img" src="${img}" alt="${title}" width="90" height="90" loading="lazy">
            </span>
            <span class="ds-bundle-upsell__name">${title}</span>
            <span class="ds-bundle-upsell__price">${this.esc(price)}</span>
          </button>
          <button type="button" class="ds-bundle-upsell__btn"
            data-variant-id="${variant.id}"
            aria-label="Voeg ${title} toe aan je bundel">
            <span class="ds-bundle-upsell__btn-label" aria-hidden="true">+</span>
            <span class="ds-bundle-upsell__spinner" aria-hidden="true"></span>
          </button>
        </div>`;
    }).join('');
  }

  sizedImage(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
  }

  // Standaard Shopify money-formatter (cents → weergave volgens shop.money_format).
  money(cents) {
    const fmt = this.dataset.moneyFormat || '€{{amount}}';
    const num = (value, precision, thousands, decimal) => {
      value = (parseInt(value, 10) / 100).toFixed(precision);
      let [i, d] = value.split('.');
      i = i.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return d ? i + decimal + d : i;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, (_m, key) => {
      switch (key) {
        case 'amount': return num(cents, 2, ',', '.');
        case 'amount_with_comma_separator': return num(cents, 2, '.', ',');
        case 'amount_no_decimals': return num(cents, 0, ',', '.');
        case 'amount_no_decimals_with_comma_separator': return num(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return num(cents, 2, "'", '.');
        default: return num(cents, 2, ',', '.');
      }
    });
  }

  openModal(trigger) {
    const m = this.modal;
    if (!m) return;
    const d = trigger.dataset;
    const setText = (sel, val) => { const el = m.querySelector(sel); if (el) el.textContent = val || ''; };

    const img = m.querySelector('.ds-bu-modal__img');
    if (img) { img.src = d.img || ''; img.alt = d.title || ''; }
    setText('.ds-bu-modal__title', d.title);
    setText('.ds-bu-modal__price', d.price);

    const cmp = m.querySelector('.ds-bu-modal__compare');
    if (cmp) {
      if (d.compare) { cmp.textContent = d.compare; cmp.hidden = false; }
      else { cmp.hidden = true; }
    }
    const desc = m.querySelector('.ds-bu-modal__desc');
    if (desc) { desc.textContent = d.desc || ''; desc.hidden = !d.desc; }

    const link = m.querySelector('.ds-bu-modal__link');
    if (link) link.href = d.url || '#';

    const addBtn = m.querySelector('.ds-bu-modal__add');
    if (addBtn) addBtn.dataset.variantId = d.variantId || '';

    m.hidden = false;
    // Focus het sluitkruisje voor toegankelijkheid.
    const close = m.querySelector('.ds-bu-modal__close');
    if (close) close.focus();
  }

  closeModal() {
    if (this.modal) this.modal.hidden = true;
  }

  async addItem(btn) {
    const id = parseInt(btn.dataset.variantId, 10);
    if (!id || btn.disabled) return;
    const discount = this.dataset.discount || '15';
    const drawer = document.querySelector('cart-drawer');
    const sections = drawer ? drawer.getSectionsToRender().map((s) => s.id) : ['cart-drawer', 'cart-icon-bubble'];
    const addUrl = (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : '/cart/add';

    btn.disabled = true;
    btn.classList.add('loading');
    if (this.errEl) this.errEl.hidden = true;

    try {
      const resp = await fetch(`${addUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{
            id,
            quantity: 1,
            properties: { _bundle_id: `bundle-drawer-${Date.now()}`, _bundle_discount: String(discount) },
          }],
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const data = await resp.json();
      if (!resp.ok || data.status) {
        if (this.errEl) {
          this.errEl.textContent = data.description || 'Er ging iets mis. Probeer het opnieuw.';
          this.errEl.hidden = false;
        }
        return;
      }
      if (drawer && data.sections) {
        drawer.renderContents(data);
      } else {
        window.location.href = (window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart';
      }
    } catch (e) {
      if (this.errEl) {
        this.errEl.textContent = 'Er ging iets mis. Probeer het opnieuw.';
        this.errEl.hidden = false;
      }
    } finally {
      btn.disabled = false;
      btn.classList.remove('loading');
    }
  }
}

if (!customElements.get('ds-bundle-upsell')) customElements.define('ds-bundle-upsell', DsBundleUpsell);
